import React from "react";
import { view } from "@orderofchaos/mvvm";
import styles from "./SearchResults.module.css";
import { EmptyImage } from "@/components/EmptyImage/EmptyImage";
import { SearchResultsViewModel } from "./SearchResultsViewModel";
import Icon, { IconSize } from "@/components/Icon/Icon";
import { Measures } from "@/types";
import type { SearchResultItem } from "@/stores/store";
import { formatTypeTag, normalizeTypeTags } from "@/utils/typeTags";

function formatQuantity(value: number, measure: Measures): string {
  const safeValue = Number.isFinite(value) ? value : 1;

  if (measure === Measures.weight) {
    return `${safeValue} кг`;
  }
  if (measure === Measures.liters) {
    return `${safeValue} л`;
  }
  return `${safeValue} шт`;
}

function formatMeasureLabel(measure: Measures): string {
  if (measure === Measures.weight) {
    return "Вес";
  }
  if (measure === Measures.liters) {
    return "Объем";
  }
  return "Количество";
}

function resultTypeLabel(item: SearchResultItem): string {
  return item.kind === "box" ? "Коробка" : "Товар";
}

function resultTypeIcon(item: SearchResultItem): "Bag" | "File" {
  return item.kind === "box" ? "Bag" : "File";
}

function resolveTypeTags(value: { typeTags?: string[] }): string[] {
  const tags = normalizeTypeTags(value.typeTags);
  if (tags.length > 0) {
    return tags;
  }

  return [];
}

export const SearchResults: React.FC = view(SearchResultsViewModel)(({
  viewModel: {
    isSearchLoading,
    searchError,
    searchItems,
    openSearchItem,
    openProductTag,
    openBoxTag,
    openProductMeasure,
    openProductLocation,
    openBoxParent,
  },
}) => {
  if (isSearchLoading) {
    return (
      <div className={`${styles.root} ${styles.stateRoot}`}>
        <div className={styles.stateCard}>
          <Icon
            name={"Search"}
            size={IconSize.large}
            loading
            className={styles.stateIcon}
          />
          <strong className={styles.stateTitle}>Ищем товары и коробки</strong>
          <span className={styles.stateDescription}>
            Проверяем склад и собираем совпадения
          </span>
        </div>
      </div>
    );
  }

  if (searchError) {
    return (
      <div className={`${styles.root} ${styles.stateRoot}`}>
        <div className={styles.stateCard}>
          <Icon
            name={"InfoBalloon"}
            size={IconSize.large}
            className={styles.stateIcon}
          />
          <strong className={styles.stateTitle}>Ошибка поиска</strong>
          <span className={styles.stateDescription}>{searchError}</span>
        </div>
      </div>
    );
  }

  if (searchItems.length === 0) {
    return (
      <div className={`${styles.root} ${styles.stateRoot}`}>
        <div className={styles.stateCard}>
          <Icon
            name={"Search"}
            size={IconSize.large}
            className={styles.stateIcon}
          />
          <strong className={styles.stateTitle}>Ничего не найдено</strong>
          <span className={styles.stateDescription}>
            Попробуйте изменить запрос или фильтры
          </span>
        </div>
      </div>
    );
  }

  return (
    <section className={styles.root}>
      <div className={styles.listMeta}>
        <strong>Найдено: {searchItems.length}</strong>
        <span className={styles.listHint}>
          Нажмите на карточку, чтобы открыть
        </span>
      </div>
      <ul className={styles.list}>
        {searchItems.map((searchItem) => {
          if (searchItem.kind === "box") {
            const box = searchItem.item;
            const typeTags = resolveTypeTags(box);
            const description = box.description.trim() || "Без описания";
            const location = box.parent?.name
              ? `Внутри: ${box.parent.name}`
              : box.parentId
                ? "Внутри другой коробки"
                : "Верхний уровень";

            return (
              <li key={box.id} className={styles.item}>
                <article className={styles.card}>
                  <button
                    type="button"
                    className={styles.cardOpen}
                    onClick={() => openSearchItem(searchItem)}
                  >
                    <div className={styles.preview}>
                      {box.photo ? (
                        <img
                          alt={box.name}
                          src={box.photo}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className={styles.entityPlaceholder}>
                          <Icon name={"Bag"} size={IconSize.big} />
                        </div>
                      )}
                    </div>
                    <div className={styles.content}>
                      <div className={styles.cardHeader}>
                        <strong className={styles.title}>
                          {box.name || "Без названия"}
                        </strong>
                        <span
                          className={`${styles.entityTypeBadge} ${styles.entityTypeBox}`}
                        >
                          <Icon name={"Bag"} size={IconSize.tiny} />
                          {resultTypeLabel(searchItem)}
                        </span>
                      </div>
                      <p className={styles.description}>{description}</p>
                    </div>
                  </button>

                  <div className={styles.metaRow}>
                    {typeTags.length > 0 ? (
                      typeTags.map((tag) => (
                        <button
                          key={`${box.id}-tag-${tag}`}
                          type="button"
                          className={`${styles.metaTag} ${styles.metaTagButton}`}
                          onClick={() => openBoxTag(tag)}
                        >
                          {formatTypeTag(tag)}
                        </button>
                      ))
                    ) : (
                      <span className={styles.metaTag}>без тега</span>
                    )}
                    <button
                      type="button"
                      className={`${styles.metaTag} ${styles.metaTagButton}`}
                      onClick={() => openBoxParent(box)}
                    >
                      {location}
                    </button>
                  </div>
                </article>
              </li>
            );
          }

          const item = searchItem.item;
          const typeTags = resolveTypeTags(item);
          const description = item.description.trim() || "Без описания";
          const location = item.parent?.name
            ? `В коробке: ${item.parent.name}`
            : item.parentId
              ? "В коробке"
              : "Без коробки";
          const quantity = formatQuantity(item.quantity, item.measure);
          const measureLabel = formatMeasureLabel(item.measure);

          return (
            <li key={item.id} className={styles.item}>
              <article className={styles.card}>
                <button
                  type="button"
                  className={styles.cardOpen}
                  onClick={() => openSearchItem(searchItem)}
                >
                  <div className={styles.preview}>
                    {item.photo ? (
                      <img
                        alt={item.name}
                        src={item.photo}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <EmptyImage />
                    )}
                  </div>
                  <div className={styles.content}>
                    <div className={styles.cardHeader}>
                      <strong className={styles.title}>
                        {item.name || "Без названия"}
                      </strong>
                      <span
                        className={`${styles.entityTypeBadge} ${styles.entityTypeProduct}`}
                      >
                        <Icon
                          name={resultTypeIcon(searchItem)}
                          size={IconSize.tiny}
                        />
                        {resultTypeLabel(searchItem)}
                      </span>
                    </div>
                    <p className={styles.description}>{description}</p>
                  </div>
                </button>
                <div className={styles.metaRow}>
                  {typeTags.length > 0 ? (
                    typeTags.map((tag) => (
                      <button
                        key={`${item.id}-tag-${tag}`}
                        type="button"
                        className={`${styles.metaTag} ${styles.metaTagButton}`}
                        onClick={() => openProductTag(tag)}
                      >
                        {formatTypeTag(tag)}
                      </button>
                    ))
                  ) : (
                    <span className={styles.metaTag}>без тега</span>
                  )}
                  <button
                    type="button"
                    className={`${styles.metaTag} ${styles.metaTagButton}`}
                    onClick={() => openProductMeasure(item.measure)}
                  >
                    {measureLabel}
                  </button>
                  <button
                    type="button"
                    className={`${styles.metaTag} ${styles.metaTagButton}`}
                    onClick={() => openProductMeasure(item.measure)}
                  >
                    {quantity}
                  </button>
                  <button
                    type="button"
                    className={`${styles.metaTag} ${styles.metaTagButton}`}
                    onClick={() => openProductLocation(item)}
                  >
                    {location}
                  </button>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
});
